import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Check, X, Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { describeDevice } from "@/lib/deviceInfo";

/**
 * Sidebar footer widget — lets the signed-in user nickname the CURRENT device
 * ("Konstantinos-iPhone", "Bridge iPad", etc.). The nickname is stored keyed by
 * the persistent `device_id` cookie, so it survives across sessions on this
 * browser and shows up in the Developer's Users panel next to the device
 * family (auto-detected from User-Agent).
 *
 * We cache the last-set label locally so the button reflects reality without
 * an API round-trip on every render.
 */
const LS_KEY = "yacht_device_label_v1";

export default function DeviceLabelBadge() {
  const { setDeviceLabel } = useAuth();
  const [dev, setDev] = useState({ family: "This device", browser: "", label: "" });
  const [saved, setSaved] = useState(localStorage.getItem(LS_KEY) || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => setDev(await describeDevice()))(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const value = draft.trim().slice(0, 80);
      await setDeviceLabel(value);
      setSaved(value);
      localStorage.setItem(LS_KEY, value);
      setEditing(false);
      toast.success(value ? `This device is now "${value}"` : "Device nickname cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save device nickname");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-2 space-y-1.5" data-testid="device-label-widget">
      <div className="flex items-center gap-1.5 text-[10px] text-primary/50 uppercase tracking-wider">
        <Smartphone className="h-3 w-3" /> This device
      </div>
      <div className="text-xs text-muted-foreground truncate" title={dev.label}>{dev.label}</div>
      {editing ? (
        <div className="flex gap-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            autoFocus
            maxLength={80}
            placeholder="Konstantinos-iPhone"
            className="flex-1 min-w-0 text-xs border border-border px-2 py-1 bg-background focus:outline-none focus:border-accent"
            data-testid="device-label-input"
          />
          <button onClick={save} disabled={busy} className="p-1 border border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10"
                  data-testid="device-label-save"><Check className="h-3 w-3" /></button>
          <button onClick={() => { setEditing(false); setDraft(saved); }} className="p-1 border border-border text-muted-foreground hover:bg-muted"
                  data-testid="device-label-cancel"><X className="h-3 w-3" /></button>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="w-full flex items-center justify-between text-xs px-2 py-1 border border-dashed border-border hover:border-accent hover:text-accent group"
          data-testid="device-label-edit"
        >
          <span className="truncate">{saved || <span className="text-muted-foreground italic">Name this device…</span>}</span>
          <Pencil className="h-3 w-3 opacity-40 group-hover:opacity-100 flex-shrink-0 ml-2" />
        </button>
      )}
    </div>
  );
}
